import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { HiCheckCircle, HiXCircle, HiRefresh } from '../components/icons';
import { showToast } from '../utils/toast';
import { Card, CardBody } from '../components/ui';
import { useOAuthCallback } from '../hooks/useAuth';

export function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing your login...');
  const hasProcessedCallback = useRef(false);

  const oauthCallbackMutation = useOAuthCallback();

  useEffect(() => {
    if (hasProcessedCallback.current) {
      return;
    }

    const handleCallback = () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Handle OAuth errors
      if (error) {
        hasProcessedCallback.current = true;
        setStatus('error');
        setMessage(errorDescription || `Authentication failed: ${error}`);
        showToast.error(`Login failed: ${error}`);

        // Redirect to login after delay
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
        return;
      }

      // Handle missing code or state
      if (!code || !state) {
        hasProcessedCallback.current = true;
        setStatus('error');
        setMessage('Invalid authentication response - missing required parameters');
        showToast.error('Authentication failed - invalid response');

        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
        return;
      }

      // Use the hook to handle the callback
      hasProcessedCallback.current = true;
      oauthCallbackMutation.mutate(
        { code, state },
        {
          onSuccess: () => {
            setStatus('success');
            showToast.success('Welcome to XTND Juniper!');

            // Redirect to dashboard
            setTimeout(() => {
              navigate('/tenants', { replace: true });
            }, 2000);
          },
          onError: error => {
            console.error('OAuth callback error:', error);
            setStatus('error');
            setMessage(error instanceof Error ? error.message : 'Authentication failed');
            showToast.error('Login failed. Please try again.');

            // Redirect to login after delay
            setTimeout(() => {
              navigate('/login', { replace: true });
            }, 3000);
          },
        }
      );
    };

    handleCallback();
  }, [navigate, oauthCallbackMutation, searchParams]);

  const getStatusIcon = () => {
    switch (status) {
      case 'processing':
        return <HiRefresh className="h-12 w-12 text-xtnd-blue animate-spin" />;
      case 'success':
        return <HiCheckCircle className="h-12 w-12 text-green-500" />;
      case 'error':
        return <HiXCircle className="h-12 w-12 text-red-500" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'processing':
        return 'text-xtnd-blue';
      case 'success':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-xtnd-blue to-xtnd-dark flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full mb-4">
            <svg className="h-8 w-8 text-xtnd-blue" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2L2 7v10c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7l-10-5z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">XTND Juniper</h1>
          <p className="text-xtnd-light text-sm">Legal Invoice Automation Platform</p>
        </div>

        {/* Status Card */}
        <Card variant="default" className="shadow-xl">
          <CardBody className="text-center py-12">
            <div className="flex justify-center mb-6">{getStatusIcon()}</div>

            <h2 className={`text-xl font-semibold mb-4 ${getStatusColor()}`}>
              {status === 'processing' && 'Processing Login'}
              {status === 'success' && 'Login Successful'}
              {status === 'error' && 'Login Failed'}
            </h2>

            <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>

            {status === 'error' && (
              <div className="space-y-2">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  You will be redirected to the login page shortly.
                </p>
                <button
                  onClick={() => navigate('/login', { replace: true })}
                  className="text-sm text-xtnd-blue hover:text-xtnd-dark underline"
                >
                  Go to login page now
                </button>
              </div>
            )}

            {status === 'processing' && (
              <div className="flex justify-center">
                <div className="animate-pulse flex space-x-1">
                  <div className="w-2 h-2 bg-xtnd-blue rounded-full"></div>
                  <div className="w-2 h-2 bg-xtnd-blue rounded-full animation-delay-200"></div>
                  <div className="w-2 h-2 bg-xtnd-blue rounded-full animation-delay-400"></div>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
